import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE = `${ENV_API_BASE_URL}/api`;

const EMPTY_FORM = {
  course: "",
  startDate: "",
  endDate: "",
  capacity: "",
  sharedCapacity: "",
  privateCapacity: "",
  coupleCapacity: "",
  duplexCapacity: "",
  priceShared: "",
  priceSharedOld: "",
  pricePrivate: "",
  pricePrivateOld: "",
  priceCouple: "",
  priceCoupleOld: "",
  priceDuplex: "",
  priceDuplexOld: "",
  status: "",
};

const formatDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const formatDateDisplay = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
};

const roomOptionMap = (batch) =>
  new Map((batch.roomOptions || []).map((item) => [item.type, item]));

function RoomSummary({ batch, type }) {
  const option = roomOptionMap(batch).get(type);
  if (!option) return <span className="text-muted">Not set</span>;

  return (
    <div>
      <div>{option.price || "-"}</div>
      <small className="text-muted">
        {option.availableSeats}/{option.capacity} seats left
      </small>
    </div>
  );
}

function CourseBatches() {
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchBatches();
  }, []);

  const fetchCourses = async () => {
    const res = await axios.get(`${API_BASE}/courses`);
    setCourses(res.data?.data || []);
  };

  const fetchBatches = async () => {
    const res = await axios.get(`${API_BASE}/course-batches`);
    setBatches(res.data?.data || []);
  };

  const courseMap = useMemo(
    () => new Map(courses.map((course) => [course._id, course])),
    [courses]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!formData.course) {
        throw new Error("Please select a course.");
      }

      const payload = {
        ...formData,
        capacity: Number(formData.capacity || 0),
        sharedCapacity: Number(formData.sharedCapacity || 0),
        privateCapacity: Number(formData.privateCapacity || 0),
        coupleCapacity: Number(formData.coupleCapacity || 0),
        duplexCapacity: Number(formData.duplexCapacity || 0),
      };

      if (editId) {
        await axios.put(`${API_BASE}/course-batches/${editId}`, payload);
      } else {
        await axios.post(`${API_BASE}/course-batches`, payload);
      }

      await fetchBatches();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save batch.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    const courseId = typeof item.course === "object" ? item.course._id : item.course;
    setFormData({
      course: courseId || "",
      startDate: formatDateInput(item.startDate),
      endDate: formatDateInput(item.endDate),
      capacity: item.capacity ?? "",
      sharedCapacity: item.sharedCapacity ?? "",
      privateCapacity: item.privateCapacity ?? "",
      coupleCapacity: item.coupleCapacity ?? "",
      duplexCapacity: item.duplexCapacity ?? "",
      priceShared: item.priceShared || "",
      priceSharedOld: item.priceSharedOld || "",
      pricePrivate: item.pricePrivate || "",
      pricePrivateOld: item.pricePrivateOld || "",
      priceCouple: item.priceCouple || "",
      priceCoupleOld: item.priceCoupleOld || "",
      priceDuplex: item.priceDuplex || item.priceDuelex || "",
      priceDuplexOld: item.priceDuplexOld || item.priceDuelexOld || "",
      status: item.status || "",
    });
    setEditId(item._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this batch?")) return;
    try {
      await axios.delete(`${API_BASE}/course-batches/${id}`);
      await fetchBatches();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete batch.");
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setShowForm(false);
    setError("");
  };

  return (
    <div className="container pt-5 mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1">Course Batches</h3>
          <p className="text-muted mb-0">
            Manage batch dates, room-type pricing, and live seat availability.
          </p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {!showForm ? (
        <>
          <button className="btn btn-primary mb-3" onClick={() => setShowForm(true)}>
            + Create Batch
          </button>

          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Course</th>
                  <th>Date Range</th>
                  <th>Total Seats</th>
                  <th>Seats Left</th>
                  <th>Shared</th>
                  <th>Private</th>
                  <th>Couple</th>
                  <th>Duplex</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {batches.map((item, index) => {
                  const courseId = typeof item.course === "object" ? item.course._id : item.course;
                  const courseTitle =
                    (typeof item.course === "object" && item.course.title) ||
                    courseMap.get(courseId)?.title ||
                    "Unknown Course";

                  return (
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td>{courseTitle}</td>
                      <td>
                        {formatDateDisplay(item.startDate)} - {formatDateDisplay(item.endDate)}
                      </td>
                      <td>{item.capacity}</td>
                      <td>{item.availableSeats}</td>
                      <td>
                        <RoomSummary batch={item} type="shared" />
                      </td>
                      <td>
                        <RoomSummary batch={item} type="private" />
                      </td>
                      <td>
                        <RoomSummary batch={item} type="couple" />
                      </td>
                      <td>
                        <RoomSummary batch={item} type="duplex" />
                      </td>
                      <td>{item.status || item.statusLabel || "Auto"}</td>
                      <td>
                        <button
                          className="btn btn-warning btn-sm me-2"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(item._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
          <div className="d-flex justify-content-between mb-3">
            <div>
              <h5 className="mb-1">{editId ? "Edit Batch" : "Create Batch"}</h5>
              <p className="text-muted small mb-0">
                If room-type capacities are filled, their total becomes the batch capacity.
                If they are left blank, the common total capacity is shared across all room types.
              </p>
            </div>
            <button type="button" className="btn-close" onClick={resetForm} />
          </div>

          <label className="form-label fw-semibold">Course</label>
          <select
            name="course"
            className="form-select mb-3"
            value={formData.course}
            onChange={handleChange}
            required
          >
            <option value="">Select Course</option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>
                {course.title}
              </option>
            ))}
          </select>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Start Date</label>
              <input
                type="date"
                name="startDate"
                className="form-control"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">End Date</label>
              <input
                type="date"
                name="endDate"
                className="form-control"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Common Capacity</label>
              <input
                type="number"
                name="capacity"
                className="form-control"
                value={formData.capacity}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Status Override</label>
              <input
                type="text"
                name="status"
                className="form-control"
                value={formData.status}
                onChange={handleChange}
                placeholder="Waiting List / Fully Booked"
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Shared Seats</label>
              <input
                type="number"
                name="sharedCapacity"
                className="form-control"
                value={formData.sharedCapacity}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Private Seats</label>
              <input
                type="number"
                name="privateCapacity"
                className="form-control"
                value={formData.privateCapacity}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Couple Seats</label>
              <input
                type="number"
                name="coupleCapacity"
                className="form-control"
                value={formData.coupleCapacity}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Duplex Seats</label>
              <input
                type="number"
                name="duplexCapacity"
                className="form-control"
                value={formData.duplexCapacity}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Shared Price</label>
              <input
                type="text"
                name="priceShared"
                className="form-control"
                value={formData.priceShared}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Shared Old Price</label>
              <input
                type="text"
                name="priceSharedOld"
                className="form-control"
                value={formData.priceSharedOld}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Private Price</label>
              <input
                type="text"
                name="pricePrivate"
                className="form-control"
                value={formData.pricePrivate}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Private Old Price</label>
              <input
                type="text"
                name="pricePrivateOld"
                className="form-control"
                value={formData.pricePrivateOld}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Couple Price</label>
              <input
                type="text"
                name="priceCouple"
                className="form-control"
                value={formData.priceCouple}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Couple Old Price</label>
              <input
                type="text"
                name="priceCoupleOld"
                className="form-control"
                value={formData.priceCoupleOld}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Duplex Price</label>
              <input
                type="text"
                name="priceDuplex"
                className="form-control"
                value={formData.priceDuplex}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">Duplex Old Price</label>
              <input
                type="text"
                name="priceDuplexOld"
                className="form-control"
                value={formData.priceDuplexOld}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-success" disabled={loading}>
              {loading ? "Saving..." : editId ? "Update" : "Save"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CourseBatches;
